import { Disclosure, Menu } from "@headlessui/react";
import {
  Bars3Icon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { UserCircleIcon } from "@heroicons/react/24/solid";
import { Button, Modal } from "antd";
import { useState } from "react";
import { Link, useLocation } from "react-router";
import { changelog } from "../data/changelog";
import { useUser, isAdmin, isTestUser } from "../UserProvider";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const NavBar = ({ currentPage }) => {
  const location = useLocation();
  const { user } = useUser();
  const [isChangelogVisible, setIsChangelogVisible] = useState(false);

  const navigation = [
    { name: "Submit Pipeline Content", href: "/submit" },
    { name: "Explore Content Catalog", href: "/catalog" },
    { name: 'Review My Submissions', href: '/my-submissions' },
    ...(isTestUser(user) ? [
      { name: 'Search (beta)', href: '/search' }
    ] : [])
  ];

  return (
    <Disclosure as="nav" className="bg-gray-800">
      {({ open }) => (
        <>
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Link to="/">
                    <img
                      className="h-10"
                      src="/ma_priceless_standard_rev.png"
                      alt="Priceless"
                    />
                  </Link>
                </div>
                <div className="hidden lg:block">
                  <div className="ml-10 flex items-baseline space-x-4">
                    {navigation.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={classNames(
                          location.pathname === item.href
                            ? "bg-gray-900 text-white"
                            : "text-gray-300 hover:bg-gray-700 hover:text-white",
                          "mcfont whitespace-nowrap rounded-md px-3 py-2 text-md font-bold"
                        )}
                        aria-current={
                          location.pathname === item.href ? "page" : undefined
                        }
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="ml-4 flex items-center md:ml-6">
                  {/* Profile dropdown */}
                  <Menu as="div" className="relative ml-3">
                    <div>
                      <Menu.Button className="flex max-w-xs items-center rounded-full bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-white pr-2 focus:ring-offset-2 focus:ring-offset-gray-800">
                        <span className="sr-only">Open user menu</span>
                        <UserCircleIcon
                          className="h-8 w-8 text-gray-300"
                          aria-hidden="true"
                        />
                        <span className="text-gray-300 mcfont ml-2 rounded-md text-md font-bold">
                          {user.firstName} {user.lastName}
                          {isAdmin(user) && " (Admin)"}
                        </span>
                      </Menu.Button>
                    </div>
                    <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => setIsChangelogVisible(true)}
                            className={classNames(
                              active ? "bg-orange-50" : "",
                              "mcfont flex w-full items-center px-4 py-2 text-sm font-semibold text-gray-700"
                            )}
                          >
                            <SparklesIcon
                              className="mr-2 h-5 w-5 text-[#CF4500]"
                              aria-hidden="true"
                            />
                            What's New
                          </button>
                        )}
                      </Menu.Item>
                    </Menu.Items>
                  </Menu>
                </div>
              </div>
              <div className="-mr-2 flex lg:hidden">
                {/* Mobile menu button */}
                <Disclosure.Button className="inline-flex items-center justify-center rounded-md bg-gray-800 p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>
            </div>
          </div>

          <Disclosure.Panel className="lg:hidden">
            <div className="space-y-1 px-2 pb-3 pt-2 sm:px-3">

              {navigation.map((item) => (
                <Disclosure.Button
                  key={item.name}
                  as={Link}
                  to={item.href}
                  className={classNames(
                    location.pathname === item.href
                      ? "bg-gray-900 text-white"
                      : "text-gray-300 hover:bg-gray-700 hover:text-white",
                    "mcfont block rounded-md px-3 py-2 text-base font-semibold"
                  )}
                  aria-current={
                    location.pathname === item.href ? "page" : undefined
                  }
                >
                  {item.name}
                </Disclosure.Button>
              ))}
            </div>
            <div className="border-t border-gray-700 pb-3 pt-4">
              <div className="flex items-center px-5">
                <div className="flex-shrink-0">
                  <UserCircleIcon
                    className="h-10 w-10 text-gray-300"
                    aria-hidden="true"
                  />
                </div>
                <div className="ml-3">
                  <div className="mcfont font-medium leading-none text-white">
                    {user.firstName} {user.lastName}
                  </div>
                  <div className="text-md mt-1 font-medium mcfont leading-none text-gray-400">
                    {user.email}
                  </div>
                </div>
              </div>
              <div className="mt-3 px-2 sm:px-3">
                <button
                  type="button"
                  onClick={() => setIsChangelogVisible(true)}
                  className="mcfont flex w-full items-center rounded-md px-3 py-2 text-base font-semibold text-gray-300 hover:bg-gray-700 hover:text-white"
                >
                  <SparklesIcon
                    className="mr-2 h-5 w-5 text-orange-400"
                    aria-hidden="true"
                  />
                  What's New
                </button>
              </div>
            </div>
          </Disclosure.Panel>

          <Modal
            title={
              <div className="mcfont text-2xl font-semibold">What's New</div>
            }
            open={isChangelogVisible}
            width={640}
            onCancel={() => setIsChangelogVisible(false)}
            footer={[
              <Button key="back" onClick={() => setIsChangelogVisible(false)}>
                Return
              </Button>,
            ]}
          >
            <div className="mcfont max-h-[60vh] space-y-6 overflow-y-auto pr-2 pt-2">
              {changelog.map((entry) => (
                <div key={`${entry.date}-${entry.title}`}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {entry.date}
                  </p>
                  <h3 className="mt-0.5 text-base font-semibold text-gray-900">
                    {entry.title}
                  </h3>
                  <ul className="mt-1.5 list-disc space-y-1 pl-5 text-sm text-gray-700">
                    {entry.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Modal>
        </>
      )}
    </Disclosure>
  );
};

export default NavBar;
